import { useState, useCallback, useEffect, useRef } from 'react'
import { ModalController } from 'shared/Modal'
import { ModalLayout } from 'shared/ModalLayout'
import { Button } from 'shared/Button'
import Table from 'shared/Table'
import { readFile, csvToTradeItems } from 'core/utils/csvParser'
import { createBatchTrade } from 'core/api/trades'
import { TradeCustomCreationResult } from 'core/types'
import './ImportTradesModal.scss'

interface ImportTradesModalProps {
    controller: ModalController<void, { file: File, journalId: string; }>
    onSuccess: () => void
}

type FailedTrade = { row: number; instrument: string; reason: string }

type ImportState =
    | { step: 'idle' }
    | { step: 'loading' }
    | { step: 'success'; count: number }
    | { step: 'error'; message: string; failures: FailedTrade[] }

export function ImportTradesModal({ controller, onSuccess }: ImportTradesModalProps) {
    const [state, setState] = useState<ImportState>({ step: 'idle' })
    const unmountedRef = useRef(false)

    useEffect(() => {
        unmountedRef.current = false
        return () => { unmountedRef.current = true }
    }, [])

    const journalId = controller.data?.journalId ?? ''
    const file = controller.data?.file!

    const processImport = useCallback(async (importedFile: File) => {
        if (unmountedRef.current) return
        setState({step: 'loading'})

        try {
            const content = await readFile(importedFile)
            const parsed = csvToTradeItems(content, journalId)

            if (parsed.errors.length > 0) {
                if (unmountedRef.current) return
                setState({
                    step: "error",
                    message: 'Failed to parse CSV file',
                    failures: parsed.errors.map((err) => ({
                        row: err.row,
                        instrument: '-',
                        reason: err.message,
                    })),
                })
                return
            }

            if (unmountedRef.current) return
            const response = await createBatchTrade(parsed.success)
            const results: TradeCustomCreationResult[] = response.data

            const failures: FailedTrade[] = []
            let successCount = 0

            results.forEach((result, index) => {
                if (result.rejectionReason !== null) {
                    const data = parsed.success[index]
                    failures.push({
                        row: index,
                        instrument: data.instrumentAlias,
                        reason: result.rejectionReason,
                    })
                } else {
                    successCount++
                }
            })

            if (failures.length === 0) {
                if (unmountedRef.current) return
                setState({ step: 'success', count: successCount })
                onSuccess()
            } else {
                if (unmountedRef.current) return
                setState({ step: 'error', message: 'Some trades failed to import: ', failures })
                if (successCount > 0) onSuccess()
            }
        } catch (error) {
            console.error(error)
            if (unmountedRef.current) return
            setState({ step: 'error', message: 'Import failed', failures: [] })
        }
    }, [journalId, onSuccess])

    const hasStartedRef = useRef(false)

    useEffect(() => {
        if (!file || hasStartedRef.current) return

        hasStartedRef.current = true
        processImport(file)
    })

    return (
        <ModalLayout className="ImportTradesModal">
            <ModalLayout.Heading>Import Trades</ModalLayout.Heading>

            {state.step === 'idle' && <p>Waiting for a file...</p>}

            {state.step === 'loading' && <p>Importing...</p>}

            {state.step === 'success' && (
                <p>
                    <strong>Imported {state.count} trades.</strong>
                    <br/>
                    <span className="subtext">Duplicates were automatically skipped.</span>
                </p>
            )}

            {state.step === 'error' && (
                <>
                    <p>{state.message}</p>
                    {state.failures.length > 0 && (
                        <Table>
                            <Table.Header>
                                <Table.HeaderCell>Row</Table.HeaderCell>
                                <Table.HeaderCell>Instrument</Table.HeaderCell>
                                <Table.HeaderCell>Reason</Table.HeaderCell>
                            </Table.Header>
                            <Table.Body>
                                {state.failures.map((f, i) => (
                                    <tr key={i}>
                                        <td>{f.row + 1}</td>
                                        <td>{f.instrument}</td>
                                        <td>{f.reason}</td>
                                    </tr>
                                ))}
                            </Table.Body>
                        </Table>
                    )}
                </>
            )}

            {(state.step === 'success' || state.step === 'error') && (
                <ModalLayout.Buttons singleButton={true}>
                    <Button className="closeButton" onClick={() => controller.close()}>Close</Button>
                </ModalLayout.Buttons>
            )}
        </ModalLayout>
    )
}